import * as React from 'react'

import LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { FilterVariables } from './types'
import applyFilters from './utils/applyFilters'
import type { ExtraHooks } from './utils/applyFilters'
import type { Value } from './utils/defaultEvaluators'
import { logicOperators } from './utils/logicOperators'

// TODO: Refactor this as a proper store, so it only
// renders things when it needs to
export default function useMultiFilters<T>(
  getFilterValue: (model: T, fieldName: Value) => any,
  localStorageKey: string,
  extraHooks: ExtraHooks = {
    beforeFilter: () => Promise.resolve(),
    skipModelFilter: () => false,
    getDefaultFilters: () => null,
    customEvaluators: {},
  },
  options: { newEntryFlowEnabled?: boolean } = {}
) {
  const [filteredModels, setFilteredModels] = React.useState<T[]>([])
  const newEntryFlowEnabled = options.newEntryFlowEnabled ?? false

  // Typed since the factorial-form package does not export its "Value" type
  const [localFilterByValue, setLocalFilterByValue] = useLocalStorage<{
    filters: Array<MultiFilterForm['values']>
    logicOperator: Record<string, string>
    default: Array<MultiFilterForm['values']>
  }>(localStorageKey)

  const initialFilters = () => {
    if (newEntryFlowEnabled && localFilterByValue?.filters) {
      const filtersWithoutLocation = localFilterByValue.filters.filter((filter) => {
        return filter.field !== 'location'
      })

      return filtersWithoutLocation.map((local) => new MultiFilterForm(local))
    }
    return localFilterByValue?.filters?.map((local) => new MultiFilterForm(local)) || []
  }

  const { employee: currentEmployee, allMyDescendants } = useCurrentSession()

  const [filters, setFilters] = React.useState<MultiFilterForm[]>(initialFilters())

  const logicOperator = React.useRef(
    new LogicOperatorForm(
      localFilterByValue?.logicOperator || {
        logic_operator: logicOperators.and,
      }
    )
  ).current

  // TODO: This should not be async
  const onApplyFilters = async (
    models: T[],
    defaultFilter?: MultiFilterForm[],
    variables?: FilterVariables
  ) => {
    await applyFilters<T>(
      models,
      defaultFilter || filters,
      logicOperator,
      getFilterValue,
      setFilteredModels,
      extraHooks,
      currentEmployee,
      allMyDescendants,
      variables
    )

    const defaultValues = defaultFilter?.[0]
    setLocalFilterByValue({
      filters: filters.map((filter) => filter.values),
      logicOperator: logicOperator.values,
      default: defaultValues ? [defaultValues.values] : [],
    })
  }

  const onCancel = () => {
    setFilters(initialFilters())
  }

  const onRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index))
  }

  const onAddFilter = () => {
    setFilters([...filters, new MultiFilterForm({})])
  }

  const resetFilter = (nFilters: MultiFilterForm[]) => {
    setFilters(nFilters)
  }

  return {
    filteredModels,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
    resetFilter,
  }
}
